export interface BatchProcessOptions {
  /** How many items to run in parallel. Default = 5 */
  batchSize?: number;
  /** ms to wait after finishing one batch before starting the next. Default = 0 */
  delayBetweenBatches?: number;
  /** How many times to retry a failed item (in addition to the first try). Default = 0 */
  maxRetries?: number;
  /** Starting delay before the first retry, in ms. Default = 1 000 */
  initialRetryDelay?: number;
  /** Multiplier applied to the retry delay after each failed attempt. Default = 2 */
  backoffFactor?: number;
  /** Called every time an item finishes (success or hard failure). */
  onProgress?: (completed: number, total: number) => void;
}

/**
 * Generic batch runner.
 *
 * @param items        Source items (immutable).
 * @param processor    Async fn that converts an item → result. Throw to trigger retry.
 * @param options      Behaviour knobs (batch size, retries, back-off, etc.).
 * @param abortSignal  Optional AbortSignal to cancel the entire job.
 * @returns            Results in original order. Items that exhausted retries are `undefined`.
 */
export async function batchProcess<T, R>(
  items: readonly T[],
  processor: (item: T, index: number, signal: AbortSignal) => Promise<R>,
  options: BatchProcessOptions = {},
  abortSignal?: AbortSignal,
): Promise<(R | undefined)[]> {
  const {
    batchSize = 5,
    delayBetweenBatches = 0,
    maxRetries = 0,
    initialRetryDelay = 1_000,
    backoffFactor = 2,
    onProgress,
  } = options;

  const results: (R | undefined)[] = new Array(items.length);
  let completed = 0;

  // Helper that runs one item with retry / back-off
  const runWithRetry = async (item: T, i: number): Promise<void> => {
    let attempt = 1;
    let delay = initialRetryDelay;
    while (attempt <= maxRetries) {
      // Abort check
      if (abortSignal?.aborted) throw new DOMException('Aborted', 'AbortError');

      try {
        results[i] = await processor(
          item,
          i,
          abortSignal ?? new AbortController().signal,
        );
        break; // success!
      } catch {
        if (attempt === maxRetries || abortSignal?.aborted) {
          // Give up – leave results[i] as undefined
          break;
        }
        await new Promise((r) => setTimeout(r, delay));
        delay *= backoffFactor;
        attempt += 1;
      }
    }

    completed += 1;
    onProgress?.(completed, items.length);
  };

  // Slice items into fixed-size batches
  for (let start = 0; start < items.length; start += batchSize) {
    const batch = items.slice(start, start + batchSize);
    // Run this batch concurrently
    await Promise.all(
      batch.map((item, idx) => runWithRetry(item, start + idx)),
    );

    if (delayBetweenBatches && start + batchSize < items.length) {
      if (abortSignal?.aborted) throw new DOMException('Aborted', 'AbortError');
      await new Promise((r) => setTimeout(r, delayBetweenBatches));
    }
  }

  return results;
}
